import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initialized, setInitialized] = useState(false);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                withCredentials: true
            });
            setUser(response.data);
            return response.data;
        } catch (error) {
            setUser(null);
            return null;
        } finally {
            setLoading(false);
            setInitialized(true);
        }
    }, []);

    // Email/Password Login
    const loginWithEmail = async (email, password) => {
        const response = await axios.post(
            `${API}/auth/login`,
            { email, password },
            { withCredentials: true }
        );
        setUser(response.data);
        return response.data;
    };

    // Register with email
    const register = async (email, password, name) => {
        const response = await axios.post(`${API}/auth/register`, {
            email,
            password,
            name
        });
        return response.data;
    };

    // Verify email
    const verifyEmail = async (token) => {
        const response = await axios.post(`${API}/auth/verify-email`, { token });
        return response.data;
    };

    // Resend verification
    const resendVerification = async (email) => {
        const response = await axios.post(`${API}/auth/resend-verification`, { email });
        return response.data;
    };

    // Forgot password
    const forgotPassword = async (email) => {
        const response = await axios.post(`${API}/auth/forgot-password`, { email });
        return response.data;
    };

    // Reset password
    const resetPassword = async (token, newPassword) => {
        const response = await axios.post(`${API}/auth/reset-password`, {
            token,
            new_password: newPassword
        });
        return response.data;
    };

    // Google Login - Get OAuth URL and redirect
    const loginWithGoogle = async () => {
        try {
            const redirectUri = `${window.location.origin}/auth/google/callback`;
            const response = await axios.get(`${API}/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`, {
                withCredentials: true
            });
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Failed to get Google auth URL:', error);
            throw error;
        }
    };

    // Process Google OAuth callback
    const processGoogleCallback = async (code) => {
        try {
            const redirect_uri = `${window.location.origin}/auth/google/callback`;
            const response = await axios.post(
                `${API}/auth/google/callback`,
                { code, redirect_uri },
                { withCredentials: true }
            );
            setUser(response.data);
            return response.data;
        } catch (error) {
            console.error('Google callback error:', error);
            throw error;
        }
    };

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
            window.location.href = '/login';
        }
    }, []);

    const refreshUser = async () => {
        return await checkAuth();
    };

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // ── Global Inactivity Logout (20 mins) ──
    useEffect(() => {
        if (!user) return;

        let intervalId;
        let lastActivity = Date.now();
        const INACTIVITY_LIMIT = 20 * 60 * 1000; // 20 minutes

        const updateActivity = () => {
            lastActivity = Date.now();
        };

        const checkInactivity = () => {
            if (Date.now() - lastActivity > INACTIVITY_LIMIT) {
                console.log("Logged out due to inactivity or device sleep.");
                logout();
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkInactivity(); // Check immediately on wake
            }
        };

        // Use passive listeners to prevent blocking the main thread
        window.addEventListener('mousemove', updateActivity, { passive: true });
        window.addEventListener('keydown', updateActivity, { passive: true });
        window.addEventListener('click', updateActivity, { passive: true });
        window.addEventListener('scroll', updateActivity, { passive: true });
        window.addEventListener('touchstart', updateActivity, { passive: true });
        window.addEventListener('visibilitychange', onVisibilityChange);

        // Periodically check inactivity
        intervalId = setInterval(checkInactivity, 30000);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('mousemove', updateActivity);
            window.removeEventListener('keydown', updateActivity);
            window.removeEventListener('click', updateActivity);
            window.removeEventListener('scroll', updateActivity);
            window.removeEventListener('touchstart', updateActivity);
            window.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [user, logout]);

    const value = {
        user,
        loading,
        initialized,
        // Email auth
        loginWithEmail,
        register,
        verifyEmail,
        resendVerification,
        forgotPassword,
        resetPassword,
        // Google auth
        loginWithGoogle,
        processGoogleCallback,
        // Legacy (for compatibility)
        login: loginWithGoogle,
        // Common
        logout,
        refreshUser,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || false
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
